import { Badge, Group, Paper, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import dayjs from 'dayjs'
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form'
import { SupportingDocumentSlot } from '@/pages/Super/components/LicensesField'
import type { ProfileFormValues } from './ProviderPage'

/**
 * The Profile tab's Professional Liability Coverage card: the policy name, the coverage period
 * and its Supporting Document (the license card's slot), every field optional. Badged
 * Expired once the end date is before today's local date; derived here, never stored.
 */
export function LiabilityCoverageField({
  control,
  setValue,
}: {
  control: Control<ProfileFormValues>
  setValue: UseFormSetValue<ProfileFormValues>
}) {
  const [coverageEnd, document, pendingFile] = useWatch({
    control,
    name: ['liability.coverageEnd', 'liability.document', 'liability.pendingFile'],
  })
  const expired = Boolean(coverageEnd) && dayjs(coverageEnd).isBefore(dayjs(), 'day')

  const dateField = (key: 'coverageStart' | 'coverageEnd', label: string) => (
    <Controller
      name={`liability.${key}`}
      control={control}
      render={({ field, fieldState }) => (
        <DatePickerInput
          {...field}
          value={field.value ?? null}
          label={label}
          placeholder={label}
          clearable
          clearButtonProps={{ 'aria-label': `Clear ${label}` }}
          error={fieldState.error?.message}
        />
      )}
    />
  )

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" align="center" mb="xs">
        <Text size="sm" fw={500}>
          Professional liability coverage
        </Text>
        {expired && (
          <Badge color="red" variant="light">
            Expired
          </Badge>
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
        <Controller
          name="liability.policyName"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              value={field.value ?? ''}
              label="Policy name"
              placeholder="Policy name"
              error={fieldState.error?.message}
            />
          )}
        />
        {dateField('coverageStart', 'Coverage starts')}
        {dateField('coverageEnd', 'Coverage ends')}
      </SimpleGrid>
      <Stack gap={4} mt="xs">
        <Text size="sm" fw={500}>
          Supporting document
        </Text>
        <SupportingDocumentSlot
          document={document}
          pendingFile={pendingFile}
          onPick={(file) => setValue('liability.pendingFile', file, { shouldDirty: true })}
          onClear={() => {
            // A pending pick is dropped; a saved document is cleared on the next Save.
            if (pendingFile) setValue('liability.pendingFile', null, { shouldDirty: true })
            else setValue('liability.document', null, { shouldDirty: true })
          }}
        />
      </Stack>
    </Paper>
  )
}
